import UniversalAPISelector, {
  ItemRenderProps,
  SuggestionRenderProps
} from '@root/components/UniversalAPISelector/UniversalAPISelector'
import { PermissionSet, useBusinessContext, usePermission } from '@root/context'
import {
  ContactType,
  IContact,
  SuggestContactProps,
  createContact,
  fetchContact,
  fetchContacts,
  suggestContact
} from '@root/query'
import { AnimatePresence, motion } from 'framer-motion'
import { Button, InputProps, ModalProps } from '@components'
import styled from 'styled-components'
import { FaCheck, FaMagic, FaPen } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { ContactForm } from '@root/pages/ContactsPage/ContactFormModal/ContactForm'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { FaAddressCard } from 'react-icons/fa6'

interface Props extends InputProps {
  onChange: (accountId: number) => void
  suggestionQuery?: Pick<SuggestContactProps, 'query'>
  isSuggestionEnabled?: boolean
  fetchOnlyInvoicableContacts: boolean
  noDataMessage: string
  isQuickAddEnabled?: boolean
}

export const ContactSelector: React.FC<Props> = ({
  suggestionQuery,
  isSuggestionEnabled = false,
  fetchOnlyInvoicableContacts,
  noDataMessage,
  isQuickAddEnabled = false,
  ...rest
}) => {
  const { slug } = useBusinessContext()
  const [t] = useTranslation()
  const formId = 'contact-form'
  const queryClient = useQueryClient()
  const hasPermissionForContacts = usePermission(PermissionSet.CONTACT_EDIT)

  const dataToShow = (item: IContact): string => {
    return `${item.name}`
  }

  const renderRow = (
    item,
    {
      isFocused,
      isSelected,
      onKeyDown,
      handleOnItemClick,
      handleOnEditClick,
      style,
      showEditItem
    }: ItemRenderProps<IContact>
  ) => {
    return (
      <ItemWrapper
        className={isFocused && 'keyboard-focused'}
        onKeyDown={onKeyDown}
        onClick={() => handleOnItemClick(item)}
        style={style}
        key={item.id}
        data-test="contact-selector-item"
      >
        <Item key={`contact-${item.id}`} title={item.name}>
          <ItemColumn>{item.name}</ItemColumn>
          <RightItemsWrapper>
            {showEditItem && hasPermissionForContacts && (
              <EditButtonWrapper>
                <Button
                  isSecondary={true}
                  icon={<FaPen />}
                  onClick={() => handleOnEditClick(item)}
                ></Button>
              </EditButtonWrapper>
            )}
            {isSelected && (
              <ItemColumn className="check">
                <CheckMarkWrapper>
                  <FaCheck />
                </CheckMarkWrapper>
              </ItemColumn>
            )}
          </RightItemsWrapper>
        </Item>
      </ItemWrapper>
    )
  }

  const createModalProps: Partial<ModalProps> = {
    width: 600,
    height: 800
  }

  const renderCreateForm = (onCreate: (data: IContact) => void, item: IContact) => {
    return (
      <ContactForm
        contact={item}
        createDefaults={
          fetchOnlyInvoicableContacts && !item?.id ? { is_invoicing_enabled: true } : undefined
        }
        onCreate={onCreate}
      />
    )
  }

  const renderSuggestion = (
    suggestion,
    isSuggestionEnabled,
    { handleOnItemClick }: SuggestionRenderProps<IContact>
  ) => {
    return (
      <AnimatePresence>
        {isSuggestionEnabled && suggestion && (
          <StyledContactSuggestionOuter>
            <StyledContactSuggestionInner
              onClick={() => {
                handleOnItemClick(suggestion)
              }}
            >
              <FaMagic />
              <span>{t('document.contactSuggestion', { contactName: suggestion.name })}</span>
            </StyledContactSuggestionInner>
          </StyledContactSuggestionOuter>
        )}
      </AnimatePresence>
    )
  }

  const createMutation = useMutation<IContact, unknown, string>({
    mutationFn: name =>
      createContact(
        { slug },
        {
          type: ContactType.UNSET,
          name
        }
      )
  })

  const onQuickCreate = (contactName: string, handleItemClick: (item: IContact) => void) => {
    const mutateOptions = {
      onSuccess: async (newContact: IContact) => {
        toast.success(t('general.success'))
        handleItemClick(newContact)
        queryClient.invalidateQueries({ queryKey: [slug, 'contacts'] })
      },
      onLoad: async () => {
        toast.error(t('general.loading'))
      },
      onError: async () => {
        toast.error(t('general.error'))
      }
    }

    createMutation.mutate(contactName, mutateOptions)
  }

  const quickCreate = (
    searchTerm: string,
    isFocused: boolean,
    handleItemClick: (item: IContact) => void
  ): React.ReactNode => {
    const isLoading = createMutation.isPending

    return (
      <ItemWrapper
        onClick={() => !isLoading && onQuickCreate(searchTerm, handleItemClick)}
        className={isFocused && 'keyboard-focused'}
        style={{
          opacity: isLoading ? 0.5 : 1,
          pointerEvents: isLoading ? 'none' : 'auto'
        }}
      >
        <Item key={'contact-quick-add'} title={searchTerm}>
          <ItemColumn>
            <QuickCreateHelperText>
              {t('contacts.contactSelector.quickCreate', { name: searchTerm })}
            </QuickCreateHelperText>
          </ItemColumn>
        </Item>
      </ItemWrapper>
    )
  }

  return (
    <UniversalAPISelector
      {...rest}
      getItemQueryKeyFn={itemId => [slug, 'contacts', itemId]}
      getItem={id => {
        const data = fetchContact({ slug, contactId: id })
        return data
      }}
      getItemsQueryKeyFn={filters => [slug, 'contacts', ...(filters ? [filters] : [])]}
      getItems={({ page, search }) => {
        const optionsDefault = { page, page_size: 200, search }
        const optionsDetailed = {
          ...optionsDefault,
          ...(fetchOnlyInvoicableContacts && { is_invoicing_enabled: true })
        }
        const data = fetchContacts({ slug }, optionsDetailed)
        return data
      }}
      renderRow={renderRow}
      noDataMessage={noDataMessage}
      renderEmpty={
        hasPermissionForContacts
          ? ({ openCreateModal }) => (
              <EmptyCreate>
                <span>{noDataMessage}</span>
                <Button type="button" size="sm" onClick={openCreateModal}>
                  {t('contacts.contactSelector.createContact')}
                </Button>
              </EmptyCreate>
            )
          : undefined
      }
      modalHeaderNew={t('contacts.contactSelector.modalHeaderNew')}
      modalHeaderUpdate={t('contacts.contactSelector.modalHeaderUpdate')}
      dropdownHeaderIcon={<FaAddressCard />}
      dropDownHeader={t('uas.contacts.select')}
      placeholder={t('uas.contacts.placeholder')}
      formId={formId}
      getDataToShow={dataToShow}
      renderCreateForm={hasPermissionForContacts && renderCreateForm}
      renderUpdateForm={hasPermissionForContacts && renderCreateForm}
      createModalProps={hasPermissionForContacts && createModalProps}
      updateModalProps={hasPermissionForContacts && createModalProps}
      renderSuggestion={renderSuggestion}
      isSuggestionEnabled={isSuggestionEnabled}
      fetchSuggestion={() => {
        const suggestion = suggestContact({ slug, ...suggestionQuery })
        return suggestion
      }}
      renderQuickCreate={isQuickAddEnabled && hasPermissionForContacts && quickCreate}
      handleQuickCreate={isQuickAddEnabled && hasPermissionForContacts && onQuickCreate}
    />
  )
}

const Item = styled(motion.div).attrs({
  variants: {
    hidden: {
      y: 5,
      opacity: 0
    },
    show: {
      y: 0,
      opacity: 1
    }
  }
})`
  display: flex;
  width: 100%;
  padding: 0.4rem 0.6rem;
  cursor: pointer;
  height: 35px;
`

const EditButtonWrapper = styled.div`
  display: none;
  align-self: center;
`

const ItemWrapper = styled.div`
  display: flex;
  width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  &:hover ${EditButtonWrapper} {
    display: block;
  }
  &.keyboard-focused,
  &:hover {
    background: ${({ theme }) => theme.colors.neutralGray};
  }
`

const RightItemsWrapper = styled.div`
  margin-left: auto;
  display: flex;
`

const ItemColumn = styled.div`
  align-self: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  &.id {
    min-width: 60px;
    font-size: ${({ theme }) => theme.fontSize.md}rem;
    margin-right: ${({ theme }) => theme.spacing.sm}rem;
    color: ${({ theme }) => theme.colors.metalGray};
    font-variant-numeric: tabular-nums;
    font-family: 'Roboto Mono', monospace;
  }
`
const CheckMarkWrapper = styled.div`
  width: 1.2rem;
  height: 1.2rem;
  margin: ${({ theme }) => theme.spacing.sm}rem;
  svg {
    fill: ${({ theme }) => theme.colors.success};
  }
`

const StyledContactSuggestionInner = styled(motion.div).attrs({
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  whileHover: 'hover',
  whileTap: 'tap'
})`
  display: flex;
  justify-self: flex-end;
  color: ${({ theme }) => theme.colors.AIPurple};
  gap: ${({ theme }) => theme.spacing.xs}rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`

const StyledContactSuggestionOuter = styled(motion.div).attrs({
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 }
})`
  margin-top: ${({ theme }) => theme.spacing.sm}rem;
  margin-bottom: ${({ theme }) => theme.spacing.md}rem;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  color: ${({ theme }) => theme.colors.AIPurple};
  gap: ${({ theme }) => theme.spacing.xs}rem;
  font-weight: 500;
`

const QuickCreateHelperText = styled.p`
  font-style: italic;
  margin: 0;
`

const EmptyCreate = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.metalGray};
`
